import throttle from 'lodash-es/throttle';
import { useCallback, useEffect } from 'react';

import { useIdleContext } from '@jane/kiosk/providers/lib/IdleProvider';
import type { TrackFirstTouchArgs } from '@jane/kiosk/util';
import { trackFirstTouch } from '@jane/kiosk/util';

const initialEvents = ['scroll', 'touchmove', 'touchstart', 'mousedown'];

/**
 * Tracks `scroll`, `touchmove`, and `touchStart` document events to determine whether
 * the event is an initial interaction & take followup actions.
 * @returns onInitialInteraction - This should be used for testing purposes ONLY!
 */
export const useInitialInteraction = (
  menuInfo: TrackFirstTouchArgs
): (() => void) => {
  const { activate, isIdle, hasActivated } = useIdleContext();

  const onInitialInteraction = useCallback(() => {
    const isInitialInteraction =
      /** Kiosk has not been touched yet. */
      !hasActivated ||
      /** This is the first touch after the kiosk has already gone idle. */
      isIdle();

    if (isInitialInteraction) {
      activate();
      menuInfo.onFirstTouch?.();
      trackFirstTouch(menuInfo);
    }
  }, [activate, hasActivated, menuInfo, isIdle]);

  useEffect(() => {
    const handleEvent = throttle(() => {
      onInitialInteraction();
    }, 1000);

    initialEvents.forEach((event) =>
      window.addEventListener(event, handleEvent)
    );

    return () => {
      initialEvents.forEach((event) =>
        window.removeEventListener(event, handleEvent)
      );
    };
  }, [onInitialInteraction]);

  return onInitialInteraction;
};
